/* global window */
import moment from 'moment';

/**
 * A candidate reschedule link carries the recruiter's identity in one of two shapes:
 *
 *   new    ?eventId=<holdEventId>&rescheduleToken=<token>
 *   legacy ?eventId=<holdEventId>&username=<recruiterEmail>
 *
 * Legacy is in every mail already sent, so the token wins only when it is really
 * a token: selecting the new path suppresses the legacy identity, so anything not
 * of the documented shape must fall through rather than break a working link.
 * Same reason the parameter is spelled out - a two letter name like `rt` is in
 * common use by link wrappers, and one appended by a mailer would have killed
 * every legacy link it touched.
 *
 * The token is an opaque bearer credential: never split it, parse its halves,
 * log it, re-encode it, or hand it to analytics or an error report.
 */
const RESCHEDULE_TOKEN_PARAM = 'rescheduleToken';
const LEGACY_USERNAME_PARAM = 'username';
const DEFAULT_SLOT_DURATION_MINUTES = 30;
// 32 lowercase hex, one dot, 43 base64url. The emitter's documented shape.
const RESCHEDULE_TOKEN_SHAPE = /^[0-9a-f]{32}\.[A-Za-z0-9_-]{43}$/;

/**
 * The candidate route. Exported so index.jsx mounts the route from this same
 * constant: if the two drifted the guard below would silently stop firing on the
 * one page it exists for.
 *
 * The token counts as this app's token only here. CalendarRepository's identity
 * sites are shared by both surfaces, so a `rescheduleToken` on a recruiter url is
 * somebody else's parameter and must not cost a signed-in recruiter their own
 * identity in their own app. react-router matches a Route path
 * case-insensitively, so this must too.
 */
const CANDIDATE_ROUTE_PATH = '/invite';
const CANDIDATE_ROUTE_SHAPE = new RegExp(`^${CANDIDATE_ROUTE_PATH}(/|$)`, 'i');

function getCandidateLinkToken(param, search = window.location.search, pathname = window.location.pathname) {
  if (!CANDIDATE_ROUTE_SHAPE.test(pathname)) return null;
  const value = new URLSearchParams(search).get(param);
  return value && RESCHEDULE_TOKEN_SHAPE.test(value) ? value : null;
}

function getRescheduleToken(search = window.location.search, pathname = window.location.pathname) {
  return getCandidateLinkToken(RESCHEDULE_TOKEN_PARAM, search, pathname);
}

/**
 * The recruiter email off the query string, for legacy links only. A real token
 * link has no address to send and must never fall back to a forgeable one, even
 * when both parameters appear on the same url.
 */
function getLegacyUsername(search = window.location.search, pathname = window.location.pathname) {
  if (getRescheduleToken(search, pathname)) return null;
  return new URLSearchParams(search).get(LEGACY_USERNAME_PARAM);
}

// Calendar configuration reaches us as a serialized DateTime ('2026-09-04T09:00:00'),
// while the slot grid reads a UTC time of day with moment.utc(x, 'HH:mm:ss').
// A datetime that carries an offset must therefore be converted, not sliced:
// slicing '2026-09-04T09:00:00+05:30' yields 09:00:00 for a window that really
// opens at 03:30:00 UTC, and the candidate is offered the wrong hours with no
// error. A bare time of day and an offsetless datetime are already UTC.
const UTC_OFFSET_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/i;

function toTimeOfDay(value) {
  if (typeof value !== 'string' || !value) return null;
  if (!value.includes('T')) return value.slice(0, 8) || null;
  if (UTC_OFFSET_SUFFIX.test(value)) {
    const instant = moment.utc(value, moment.ISO_8601);
    return instant.isValid() ? instant.format('HH:mm:ss') : null;
  }
  return value.split('T')[1].slice(0, 8) || null;
}

/**
 * Maps the resolve payload onto the shape the existing slot grid consumes.
 * Property names are PascalCase and any null property is omitted entirely
 * (Newtonsoft DefaultContractResolver + NullValueHandling.Ignore), so every
 * field is read defensively. An unconfigured calendar yields an empty grid.
 */
function toRescheduleView(context) {
  const resolved = context || {};
  const slotDuration = Number(resolved.SlotDurationMinutes);
  return {
    recruiterName: resolved.RecruiterName || '',
    summary: resolved.Summary || '',
    holdStartTimeUtc: resolved.StartTimeUtc || null,
    holdEndTimeUtc: resolved.EndTimeUtc || null,
    settings: {
      SlotDuration: slotDuration > 0 ? slotDuration : DEFAULT_SLOT_DURATION_MINUTES,
      AvailableWeekDays: Array.isArray(resolved.AvailableWeekDays) ? resolved.AvailableWeekDays : [],
      StartingFreeTime: toTimeOfDay(resolved.StartingFreeTime),
      EndingFreeTime: toTimeOfDay(resolved.EndingFreeTime),
    },
  };
}

/**
 * 401 is the endpoint's only refusal and covers every way a link can be bad; the
 * reasons are deliberately indistinguishable, so it gets one message and no
 * retry. Nothing from the failed request is carried into the message - the
 * request body holds the token.
 */
function describeRescheduleFailure(error) {
  const status = error && error.response ? error.response.status : null;
  if (!status || status >= 500) {
    return {
      title: 'The calendar could not be reached',
      description: 'Please try again.',
      canRetry: true,
    };
  }
  return {
    title: 'That link is not valid',
    description: 'Please ask your recruiter for a new invitation link.',
    canRetry: false,
  };
}

export {
  RESCHEDULE_TOKEN_PARAM,
  CANDIDATE_ROUTE_PATH,
  getCandidateLinkToken,
  getRescheduleToken,
  getLegacyUsername,
  toRescheduleView,
  describeRescheduleFailure,
};
